import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Tag, Button, Modal, Spin, Empty, message, Descriptions, Timeline,
} from 'antd';
import {
  EyeOutlined, PlusOutlined, StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const GOLD = '#B8892A';
const GREEN = '#2E7D4F';

const STATUS_MAP = {
  applying:        { label: '申请中',     color: 'orange',     step: 0 },
  evaluating:      { label: 'AI评估中',   color: 'processing', step: 0 },
  confirmed:       { label: '已确认出品', color: 'blue',       step: 1 },
  pickup_arranged: { label: '已安排取件', color: 'blue',       step: 1 },
  in_transit:      { label: '运输中',     color: 'cyan',       step: 2 },
  arrived_jp:      { label: '已抵日本仓', color: 'cyan',       step: 2 },
  inspecting_jp:   { label: '日本检品中', color: 'purple',     step: 3 },
  listed:          { label: '已上架',     color: 'geekblue',   step: 4 },
  auction_live:    { label: '竞拍进行中', color: 'green',      step: 4 },
  sold:            { label: '已成交',     color: 'success',    step: 5 },
  unsold:          { label: '流拍',       color: 'red',        step: 5 },
  cancelled:       { label: '已取消',     color: 'default',    step: -1 },
};

const STEP_LABELS = ['申请 · 评估', '取件 · 确认', '运输入库', '日本检品', '上拍竞价', '结算到账'];

function ProgressSteps({ status }) {
  const info  = STATUS_MAP[status] || { step: 0 };
  const curStep = info.step;
  return (
    <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
      {STEP_LABELS.map((label, i) => {
        const isDone   = i < curStep;
        const isActive = i === curStep;
        return (
          <React.Fragment key={i}>
            <div style={{ textAlign: 'center', minWidth: 60 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px',
                background: isDone ? GREEN : isActive ? GOLD : '#EDEAE3',
                color: isDone || isActive ? '#fff' : '#B0A898',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                boxShadow: isActive ? `0 0 0 4px rgba(200,146,42,.2)` : 'none',
              }}>
                {isDone ? '✓' : i + 1}
              </div>
              <div style={{
                fontSize: 10, color: isActive ? '#1A1612' : isDone ? GREEN : '#B0A898',
                fontWeight: isActive ? 600 : 400, lineHeight: 1.2,
              }}>
                {label}
              </div>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 2,
                background: isDone ? GREEN : '#EAE6DF',
                margin: '0 2px 20px',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ReverseTracking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [detail, setDetail]     = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [jpyRate, setJpyRate] = useState(0.043);
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = data?.rate; if (r && r > 0) setJpyRate(Number(r));
    }).catch(() => {});
  }, []);

  const loadList = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/consignments/reverse?page=${p}&limit=10`);
      setList(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch { message.error('加载失败'); }
    finally { setLoading(false); }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await api.get(`/consignments/reverse/${id}`);
      setDetail(res.data);
      setDetailOpen(true);
    } catch { message.error('加载详情失败'); }
  }, []);

  const handleCancel = async (id) => {
    try {
      await api.patch(`/consignments/reverse/${id}`, { status: 'cancelled' });
      message.success('已取消');
      loadList(page);
    } catch (err) { message.error(err?.response?.data?.error || '操作失败'); }
  };

  useEffect(() => { loadList(); }, [loadList]);

  const JPY_RATE = jpyRate;

  const columns = [
    {
      title: '出品单号',
      dataIndex: 'listing_no',
      render: v => <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{v}</span>,
    },
    { title: '商品', dataIndex: 'item_name', ellipsis: true },
    {
      title: '底价',
      dataIndex: 'floor_price_jpy',
      render: v => (
        <span style={{ fontFamily: 'var(--mono)' }}>
          ¥{Number(v).toLocaleString()} JPY
        </span>
      ),
    },
    {
      title: '拍卖行',
      dataIndex: 'house_name',
      render: v => v || '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: v => {
        const s = STATUS_MAP[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: '成交价',
      dataIndex: 'final_price_jpy',
      render: (v, row) => v
        ? <span style={{ fontFamily: 'var(--mono)', color: GREEN }}>¥{Number(v).toLocaleString()} JPY</span>
        : row.status === 'auction_live'
          ? <Tag color="green" style={{ animation: 'none' }}>竞拍中</Tag>
          : '—',
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      render: v => v ? new Date(v).toLocaleDateString('zh-CN') : '—',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => loadDetail(row.id)} />
          {['applying', 'evaluating'].includes(row.status) && (
            <Button
              size="small" danger icon={<StopOutlined />}
              onClick={() => Modal.confirm({
                title: '确认取消', content: '取消后该反向出品申请将关闭。',
                onOk: () => handleCancel(row.id),
              })}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, fontFamily: 'var(--serif)' }}>
            反向出品追踪
          </h1>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, padding: '3px 8px',
            background: 'rgba(46,125,79,.1)', color: GREEN,
            border: '1px solid rgba(46,125,79,.2)', borderRadius: 3, letterSpacing: 1,
          }}>
            ↩ 出品到日本
          </span>
        </div>
        <p style={{ color: '#7A7268', fontSize: 13, margin: 0 }}>
          追踪您的反向出品进度 · 从取件到日本上拍结算全程可见
        </p>
      </div>

      {/* New apply button */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: GREEN, borderColor: GREEN }}
          onClick={() => navigate('/reverse-apply')}
        >
          新建反向出品
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          dataSource={list}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            total,
            current: page,
            pageSize: 10,
            onChange: loadList,
            showSizeChanger: false,
            showTotal: t => `共 ${t} 条`,
          }}
          locale={{ emptyText: <Empty description="暂无反向出品记录" /> }}
        />
      </Spin>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetail(null); }}
        footer={null}
        title={detail ? `出品详情 · ${detail.listing_no}` : '出品详情'}
        width={700}
      >
        {detail && (
          <div>
            {/* Status Hero */}
            <div style={{
              background: '#1A1612',
              borderRadius: 4,
              padding: '16px 20px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,.4)', fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: 2, marginBottom: 4 }}>
                  当前状态
                </div>
                <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>
                  {detail.item_name}
                </div>
                <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12, marginTop: 3 }}>
                  {detail.house_name ? `目标：${detail.house_name}` : '拍卖行待定'}
                </div>
              </div>
              <Tag color={STATUS_MAP[detail.status]?.color || 'default'} style={{ fontSize: 12 }}>
                {STATUS_MAP[detail.status]?.label || detail.status}
              </Tag>
            </div>

            {/* Progress Steps */}
            <ProgressSteps status={detail.status} />

            {/* Key Info */}
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="出品单号" span={2}>
                <span style={{ fontFamily: 'var(--mono)' }}>{detail.listing_no}</span>
              </Descriptions.Item>
              <Descriptions.Item label="品相">{detail.item_grade || '—'}</Descriptions.Item>
              <Descriptions.Item label="物品位置">
                {{ cn: '客户持有', jp: '日本境内', store: '云川仓库' }[detail.item_location] || detail.item_location}
              </Descriptions.Item>
              <Descriptions.Item label="设定底价">
                <span style={{ fontFamily: 'var(--mono)' }}>
                  ¥{Number(detail.floor_price_jpy).toLocaleString()} JPY
                  <span style={{ color: '#7A7268', marginLeft: 6, fontSize: 11 }}>
                    (≈ ¥{Math.round(detail.floor_price_jpy * JPY_RATE).toLocaleString()} CNY)
                  </span>
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="平台服务费">{detail.platform_fee_pct}%</Descriptions.Item>
              {detail.ai_score != null && (
                <Descriptions.Item label="AI 综合评分">
                  <span style={{ color: GOLD, fontWeight: 700, fontSize: 16 }}>{detail.ai_score}</span>
                  <span style={{ color: '#7A7268' }}> / 100</span>
                </Descriptions.Item>
              )}
              {detail.ai_range_min_jpy && (
                <Descriptions.Item label="AI 预估区间">
                  <span style={{ fontFamily: 'var(--mono)', color: GREEN }}>
                    ¥{Number(detail.ai_range_min_jpy).toLocaleString()} ~ ¥{Number(detail.ai_range_max_jpy).toLocaleString()} JPY
                  </span>
                </Descriptions.Item>
              )}
              {detail.logistics_no && (
                <Descriptions.Item label="物流单号" span={2}>
                  <span style={{ fontFamily: 'var(--mono)' }}>{detail.logistics_no}</span>
                </Descriptions.Item>
              )}
              {detail.final_price_jpy && (
                <Descriptions.Item label="成交价">
                  <span style={{ fontFamily: 'var(--mono)', color: GREEN, fontWeight: 700 }}>
                    ¥{Number(detail.final_price_jpy).toLocaleString()} JPY
                  </span>
                </Descriptions.Item>
              )}
              {detail.settled_cny && (
                <Descriptions.Item label="实际到账">
                  <span style={{ fontFamily: 'var(--mono)', color: GOLD, fontWeight: 700 }}>
                    ¥{Number(detail.settled_cny).toLocaleString()} CNY
                  </span>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="申请时间" span={2}>
                {new Date(detail.created_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
              {detail.notes && (
                <Descriptions.Item label="备注" span={2}>{detail.notes}</Descriptions.Item>
              )}
              {detail.admin_notes && (
                <Descriptions.Item label="运营备注" span={2} style={{ color: '#C0392B' }}>
                  {detail.admin_notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Status-based actions */}
            {['applying', 'evaluating'].includes(detail.status) && (
              <div style={{ textAlign: 'right' }}>
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: '取消出品申请',
                      content: '确认取消此反向出品申请？',
                      onOk: async () => {
                        await handleCancel(detail.id);
                        setDetailOpen(false);
                      },
                    });
                  }}
                >
                  取消申请
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
